import { useId, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface FileDropZoneProps {
  label?: string;
  /**
   * Id of a caption rendered outside the component, for a caller that shows
   * its own (with a button beside it) instead of passing `label`.
   */
  labelledBy?: string;
  accept: string;
  hint: string;
  /** Currently-selected file, shown by name. Pass null for a stateless picker. */
  file?: File | null;
  onFile: (f: File) => void;
  summary?: string;
}

/**
 * Dashed drag-and-drop file picker used across the card generators (CSV, SVG
 * template, employee login list) so every upload control looks the same.
 */
export function FileDropZone({
  label,
  labelledBy,
  accept,
  hint,
  file = null,
  onFile,
  summary,
}: FileDropZoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const ownCaptionId = useId();
  const zoneId = useId();
  const captionId = labelledBy ?? (label ? ownCaptionId : undefined);
  return (
    <div>
      {label && (
        <p
          id={ownCaptionId}
          className="mb-1 block text-xs font-medium text-ink"
        >
          {label}
        </p>
      )}
      <div
        id={zoneId}
        role="button"
        tabIndex={0}
        aria-label={t('photoUpload.dropZoneLabel')}
        // The caption, then the zone's own aria-label: "CSV file Select a file".
        aria-labelledby={captionId ? `${captionId} ${zoneId}` : undefined}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) {
            onFile(f);
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className="cursor-pointer border border-dashed border-rule bg-bg-soft px-4 py-5 text-center transition-colors"
      >
        {file ? (
          <>
            <p className="text-sm font-medium text-blue">{file.name}</p>
            {summary && (
              <p className="mt-0.5 text-xs text-muted-soft">{summary}</p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted">
              <Trans
                i18nKey="cardGenerator.dropzone.prompt"
                components={{ span: <span /> }}
              />
            </p>
            <p className="mt-1 text-xs text-muted-soft">{hint}</p>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) {
            onFile(f);
          }
        }}
      />
    </div>
  );
}
